import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useToast } from '@/shared/widgets/toast';
import type { PaymentService } from '../../application/services/PaymentService';
import {
  createPaymentContextSession,
  readPaymentContextSession,
  savePaymentContextSession,
} from '../utils/paymentContextSession';
import {
  toPayViaLinkDetailsVm,
  type PayViaLinkDetailsVm,
} from '../viewModels/payViaLink.vm';
import { getPaymentSourceFromQuery } from '../utils/paymentResultUrl';

interface UsePaymentDetailsOptions {
  service: PaymentService;
  paymentContextQueryOverride?: string;
}

export function usePaymentDetails({
  service,
  paymentContextQueryOverride,
}: UsePaymentDetailsOptions) {
  const { token } = useParams<{ token: string }>();
  const location = useLocation();
  const { toast } = useToast();
  const [details, setDetails] = useState<PayViaLinkDetailsVm | undefined>();
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<unknown>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const paymentSession = useMemo(() => readPaymentContextSession(), []);

  const paymentContextQuery = useMemo(() => {
    if (paymentContextQueryOverride !== undefined) return paymentContextQueryOverride;
    const rawSearch = location.search.startsWith('?') ? location.search.slice(1) : location.search;
    if (rawSearch) return rawSearch;
    if (token) return `token=${encodeURIComponent(token)}`;
    return '';
  }, [location.search, paymentContextQueryOverride, token]);

  useEffect(() => {
    if (!paymentContextQuery) return;

    let cancelled = false;

    void Promise.resolve().then(() => {
      if (cancelled) return;
      setIsDetailsLoading(true);
      setDetailsError(null);
    });

    service
      .getPayViaLinkDetails(paymentContextQuery)
      .then((paymentDetails) => {
        if (cancelled) return;
        setDetails(toPayViaLinkDetailsVm(paymentDetails));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setDetails(undefined);
        setDetailsError(err || true);
      })
      .finally(() => {
        if (!cancelled) {
          setIsDetailsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [paymentContextQuery, service]);

  const activeDetails = paymentContextQuery ? details : undefined;
  const razorpayKey = activeDetails?.razorpayKeyId || paymentSession?.razorpayKeyId || '';
  const razorpayCustomerId = activeDetails?.razorpayCustomerId || paymentSession?.razorpayCustomerId;
  const expiresAt = activeDetails?.expiresAt || paymentSession?.expiresAt;
  const paymentSource =
    activeDetails?.paymentSource ||
    paymentSession?.paymentSource ||
    getPaymentSourceFromQuery(paymentContextQuery);
  const isExpired = Boolean(expiresAt && nowMs >= expiresAt);

  useEffect(() => {
    if (detailsError) {
      toast({
        title: 'Invalid Link',
        description: 'This POS payment link is invalid or expired.',
      });
    }
  }, [detailsError, toast]);

  useEffect(() => {
    if (!details || !paymentContextQuery || paymentContextQueryOverride !== undefined) return;

    const txnId = details.txnId || details.orderId || paymentSession?.txnId;
    const key = details.razorpayKeyId || paymentSession?.razorpayKeyId;

    if (!txnId || !key) return;

    const sessionDetails: PayViaLinkDetailsVm = {
      ...details,
      orderId: details.orderId || txnId,
      txnId,
      razorpayKeyId: key,
      razorpayCustomerId: details.razorpayCustomerId || paymentSession?.razorpayCustomerId,
      expiresAt,
    };

    savePaymentContextSession(
      createPaymentContextSession({
        details: sessionDetails,
        paymentContextQuery,
        retryPath: `${location.pathname}${location.search}`,
      })
    );
  }, [
    details,
    expiresAt,
    location.pathname,
    location.search,
    paymentContextQuery,
    paymentContextQueryOverride,
    paymentSession?.razorpayCustomerId,
    paymentSession?.razorpayKeyId,
    paymentSession?.txnId,
  ]);

  useEffect(() => {
    if (!expiresAt) return;

    const timeoutId = window.setTimeout(() => setNowMs(Date.now()), 0);
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
    };
  }, [expiresAt]);
  
  return {
    details: activeDetails,
    isDetailsLoading: Boolean(paymentContextQuery) && isDetailsLoading,
    detailsError: (paymentContextQuery && (detailsError as { message?: string } | null)?.message) || '',
    isExpired,
    paymentSession,
    paymentContextQuery,
    paymentSource,
    razorpayKey,
    razorpayCustomerId,
    expiresAt,
  };
}

